/**
 * PlanetWIR Theme - Main TypeScript Entry
 */

// Import styles
import '../css/main.css';

// Import modules
import { initA11y } from './utils/accessibility';
import { initOvniLine } from './components/OvniLine';
import { initWhiteLine } from './components/WhiteLine';
import './components/CompanyDropdown'; // Import to run initialization logic
import './components/CookieConsent'; // GDPR cookie consent banner

class App {
  constructor() {
    this.init();
  }

  private init(): void {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.onReady());
    } else {
      this.onReady();
    }
  }

  private onReady(): void {
    // Initialize accessibility features
    initA11y();

    // Initialize OVNI line animation (UFO follows cyan line on scroll)
    initOvniLine();

    // Initialize white line from curve to train
    initWhiteLine();

    // Initialize video players
    this.initVideoPlayers();

    // Initialize smooth scroll animations
    this.initScrollAnimations();

    // Log for debugging in development only
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('✅ PlanetWIR App initialized');
    }
  }

  private initVideoPlayers(): void {
    const videoContainers = document.querySelectorAll('[data-video-player]');
    videoContainers.forEach((container) => {
      const playButton = container.querySelector('[data-play-button]');
      const video = container.querySelector('video');

      if (playButton && video) {
        playButton.addEventListener('click', () => {
          if (video.paused) {
            void video.play();
            playButton.classList.add('hidden');
          } else {
            video.pause();
            playButton.classList.remove('hidden');
          }
        });

        video.addEventListener('ended', () => {
          playButton.classList.remove('hidden');
        });
      }
    });
  }

  private initScrollAnimations(): void {
    // Intersection Observer for fade-in animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe elements with data-animate attribute
    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });
  }
}

// Initialize app
new App();
